import { api_configuration } from "../../common";
import api_gui = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_app_id = api_configuration.types.application_identifier.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import { client_state } from "./ClientState";
import * as SubscriptionManager from "../state 2 interface/SubscriptionManager";
import { IdPathBuilder, stepsAreEqual, Raw_Id_Path_Head } from "../navigation/IdPathBuilder";
import DestructorTree from "../DestructorTree";
import ReferenceCounter from "../ReferenceCounter";
import garbageCollector from "../state/GarbageCollector";
import {
	addTextAttribute,
	addNumberAttribute,
	addFileAttribute,
	addGroupAttribute,
	addStategroupAttribute,
	addCollectionAttribute,
	addCommandAttribute,
	addActionAttribute,
	addRemoteEntry,
} from "../state/addClientState";
import createIdPathBuilderFromClientIdPath from "../navigation/createIdPathBuilderFromClientIdPath";
import createClientIdPathFromNode from "../navigation/createClientIdPathFromNode";
import { applyCollectionJoin } from "./CollectionJoin";
import * as Collection from "../state/Collection";
import { iReference } from "../state/Reference";
import { addChild, DestructorSet } from "../../../../../destructables";
import {
	Scalar,
	ScalarProvider,
	WeakCache,
	ScalarConsumer,
	onScalarValue,
	createVariable,
	createExpressionBuilder,
} from "../../../../../stream";
import { toStream } from "../toStream";
import { Failed, Pending, isResolved } from "../Status";
import { createIdPathFromNode } from "../navigation/createIdPathFromNode";
import * as step from "../state/step";
import * as Query from "../state/Query";
import { iEntry } from "../state/Entry";

export type RegistrationStatus = "unsubscribed" | "not subscribable" | "pending" | "failed" | "subscribed";

export const isSubscribable = WeakCache<api_cs.Cnode, ScalarProvider<boolean>>((node) => {
	return createVariable(
		toStream(node.properties.location).stateSwitch({
			local: () => false,
			remote: () =>
				toStream(node.properties.read_permitted).stateSwitch({
					no: () => false,
					yes: () => true,
				})(),
		})(),
		false
	);
});

class RegistrationVariable extends ScalarProvider<RegistrationStatus> {
	private readonly _subscribers = new Set<ScalarConsumer<RegistrationStatus>>();
	private _subscription?: ScalarConsumer<boolean>;
	private _counter_subscription?: api_cs.Subscription;
	private _destructor: DestructorTree | undefined;
	private _value: RegistrationStatus;

	constructor(private readonly object: SubscribableObject) {
		super();
		object.onDestroy(() => this.destroy());
	}

	get value() {
		if (this._subscribers.size > 0) return this._value;
		return asRegisterable(this.object).__subscriptions?.value ?? 0 > 0 ? "subscribed" : "unsubscribed";
	}

	private update(value: RegistrationStatus) {
		if (this._value === value) return;
		this._value = value;
		for (const subscriber of this._subscribers) {
			subscriber.update(value);
		}
	}

	subscribe(subscriber: ScalarConsumer<RegistrationStatus>) {
		if (this.object.destroyed) return subscriber;
		if (this._subscribers.size === 0) {
			this._destructor = new DestructorTree();
			register(this.object, this._destructor);
			this._subscription = isSubscribable(this.object.component_root).subscribe({
				update: (subscribable) => {
					switch (subscribable) {
						case false:
							this.update("not subscribable");
							break;
						case true: {
							this.update("pending");
							this._counter_subscription?.unsubscribe();
							this._counter_subscription = getSubscriptionCounter(this.object).subscribe((num_subs) => {
								if (num_subs > 0) {
									this.update("subscribed");
								}
							});
							break;
						}
					}
				},
			});
		}
		this._subscribers.add(subscriber);
		subscriber.update(this.value);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<RegistrationStatus>) {
		this._subscribers.delete(subscriber);
		this.destroy();
	}

	destroy() {
		if (this._subscribers.size === 0) {
			this._value = "unsubscribed";
			this._destructor?.destroy();
			this._destructor = undefined;
			this._counter_subscription?.unsubscribe();
			this._counter_subscription = undefined;
			if (this._subscription) {
				isSubscribable(this.object.component_root).unsubscribe(this._subscription);
				this._subscription = undefined;
			}
		}
	}
}

export const getRegistrationStatus = WeakCache<SubscribableObject, ScalarProvider<RegistrationStatus>>(
	(object) => new RegistrationVariable(object)
);

export function registrationDone<T>(
	object: SubscribableObject,
	on_registration_done: (subscribed: boolean) => Scalar<T>
) {
	return createExpressionBuilder(getRegistrationStatus(object)).pipe((registration_status) => {
		switch (registration_status) {
			case "unsubscribed":
				return Pending;
			case "pending":
				return Pending;
			case "failed":
				return Failed;
			case "not subscribable":
				return on_registration_done(false);
			case "subscribed":
				return on_registration_done(true);
		}
	});
}

export function subscriptionPending(object: SubscribableObject) {
	return createExpressionBuilder(getRegistrationStatus(object)).pipe((status) => {
		switch (status) {
			case "not subscribable":
				return false;
			case "unsubscribed":
				return true;
			case "pending":
				return true;
			case "failed":
				return false;
			case "subscribed":
				return false;
		}
	});
}

abstract class CollectionRegistrationType {
	constructor(public readonly collection: api_cs.Ccollection) {}
	get component_root(): api_cs.Cnode {
		return this.collection.component_root;
	}
	onDestroy(destruction_observer: () => void) {
		this.collection.onDestroy(destruction_observer);
	}
	get destroyed() {
		return this.collection.destroyed;
	}
}
export class CollectionKeyRegistration extends CollectionRegistrationType {}
class CollectionUpdatesRegistration extends CollectionRegistrationType {}
class CollectionEntriesRegistration extends CollectionRegistrationType {
	node = new Map<string, EntriesRegistrationAttribute>();
}
class EntriesRegistrationCommand extends CollectionRegistrationType {}
class EntriesRegistrationText extends CollectionRegistrationType {
	reference = false;
}
class EntriesRegistrationNumber extends CollectionRegistrationType {}
class EntriesRegistrationFile extends CollectionRegistrationType {}
class EntriesRegistrationGroup extends CollectionRegistrationType {
	node = new Map<string, EntriesRegistrationAttribute>();
}
class EntriesRegistrationStategroup extends CollectionRegistrationType {
	states = new Map<string, EntriesRegistrationNode>();
	constructor(collection: api_cs.Ccollection, public def: api_app.Cstate_group) {
		super(collection);
	}
}
class EntriesRegistrationCollection extends CollectionRegistrationType {
	updates: CollectionUpdatesRegistration;
	entries: CollectionEntriesRegistration;
	constructor(collection: api_cs.Ccollection) {
		super(collection);
		this.updates = new CollectionUpdatesRegistration(collection);
		this.entries = new CollectionEntriesRegistration(collection);
	}
}
type EntriesRegistrationAttribute =
	| ["command", EntriesRegistrationCommand]
	| ["text", EntriesRegistrationText]
	| ["number", EntriesRegistrationNumber]
	| ["file", EntriesRegistrationFile]
	| ["group", EntriesRegistrationGroup]
	| ["stategroup", EntriesRegistrationStategroup]
	| ["collection", EntriesRegistrationCollection];
export type EntriesRegistrationNode = Map<string, EntriesRegistrationAttribute>;

type CollectionRegistrations = {
	keys: Map<string, CollectionKeyRegistration>;
	entries: CollectionEntriesRegistration;
	updates: CollectionUpdatesRegistration;
};

export type SubscribableObject =
	| api_cs.Ccommand
	| api_cs.Ctext__type__property
	| api_cs.Cnumber__type__property
	| api_cs.Cfile__type__property
	| api_cs.Cgroup__type__property
	| api_cs.Cstate_group__type__property
	| CollectionKeyRegistration
	| CollectionEntriesRegistration
	| CollectionUpdatesRegistration
	| EntriesRegistrationCommand
	| EntriesRegistrationText
	| EntriesRegistrationNumber
	| EntriesRegistrationFile
	| EntriesRegistrationGroup
	| EntriesRegistrationStategroup;
export type CountableObject =
	| SubscribableObject
	| EntriesRegistrationCollection
	| api_cs.Cyes__initialized__query_entry_status
	| api_cs.Cyes__joined
	| api_cs.Caction
	| api_cs.Centries__collection;

type Registerable = {
	__references?: ReferenceCounter;
	__subscriptions?: ReferenceCounter;
	__needs_subscription?: boolean;
};

export function asRegisterable(object: CountableObject): Registerable {
	return object as any as Registerable;
}

export async function registrationInitialized(object: SubscribableObject): Promise<void> {
	const registration = asRegisterable(object);
	return new Promise<void>((resolve) => {
		if (!registration.__subscriptions || !registration.__references) return resolve();
		registration.__references.await((value) => value <= 0).then(() => resolve());
		registration.__subscriptions.onDestroy(() => resolve());
		registration.__subscriptions.await((value) => value > 0).then(() => resolve());
	});
}

function initReferenceCounter(): ReferenceCounter {
	const counter = new ReferenceCounter();
	counter.onEmpty(
		() => {
			garbageCollector.queue();
		},
		/*persist=*/ true,
		/*skip_first=*/ true
	);
	return counter;
}

export function getReferenceCounter(object: CountableObject) {
	const countable = asRegisterable(object);
	if (!countable.__references) {
		countable.__references = initReferenceCounter();
	}
	return countable.__references!;
}

export function getSubscriptionCounter(object: SubscribableObject): ReferenceCounter {
	const registration = asRegisterable(object);
	if (registration.__subscriptions === undefined) {
		registration.__subscriptions = new ReferenceCounter();
		registration.__subscriptions.onEmpty(
			() => {
				if (registration.__references && registration.__references.count > 0) {
					registration.__needs_subscription = true;
					SubscriptionManager.queue(object);
				}
			},
			/*persist=*/ true,
			/*skip_first=*/ false
		);
		registration.__references?.onEmpty(
			() => {
				initializedObjects.delete(object);
				registration.__subscriptions?.destroy();
				registration.__needs_subscription = false;
				registration.__subscriptions = undefined;
			},
			/*persist=*/ false,
			/*skip_first=*/ true
		);
		registration.__needs_subscription = true;
	}
	return registration.__subscriptions!;
}

export function registerSubscription(object: SubscribableObject, destructor: DestructorTree): void {
	getSubscriptionCounter(object).add_registration(destructor);
	asRegisterable(object).__needs_subscription = false;
}

export function removeSubscriptionCounter(object: SubscribableObject): void {
	const registration = asRegisterable(object);
	if (registration.__subscriptions) {
		registration.__subscriptions.destroy();
		registration.__needs_subscription = false;
		registration.__subscriptions = undefined;
	}
}

export function isRegisteredOrSubscribed(object: SubscribableObject): boolean {
	const registration = asRegisterable(object);
	return (
		(registration.__references !== undefined && registration.__references.count > 0) ||
		(registration.__subscriptions !== undefined && registration.__subscriptions.count > 0)
	);
}

export function isRegistered(object: CountableObject): boolean {
	const registration = asRegisterable(object);
	return registration.__references !== undefined && registration.__references.count > 0;
}

export function needsToBeSubscribed(object: SubscribableObject): boolean {
	const registration = asRegisterable(object);
	return !!registration.__needs_subscription;
}

export function getCollectionRegistrations(collection: api_cs.Ccollection): CollectionRegistrations {
	const patched_collection = collection as any as { __registrations?: CollectionRegistrations };
	if (patched_collection.__registrations === undefined) {
		patched_collection.__registrations = {
			keys: new Map<string, CollectionKeyRegistration>(),
			entries: new CollectionEntriesRegistration(collection),
			updates: new CollectionUpdatesRegistration(collection),
		};
	}
	return patched_collection.__registrations!;
}

export function getCollectionEntriesRegistration(collection: api_cs.Ccollection): CollectionEntriesRegistration {
	const collection_registrations = getCollectionRegistrations(collection);
	return collection_registrations.entries;
}

export function getCollectionUpdatesRegistration(collection: api_cs.Ccollection): CollectionUpdatesRegistration {
	const collection_registrations = getCollectionRegistrations(collection);
	return collection_registrations.updates;
}

export function getCollectionKeyRegistration(collection: api_cs.Ccollection, key: string): CollectionKeyRegistration {
	const collection_registrations = getCollectionRegistrations(collection);
	if (!collection_registrations.keys.has(key)) {
		collection_registrations.keys.set(key, new CollectionKeyRegistration(collection));
	}
	return collection_registrations.keys.get(key)!;
}

export function initializeKeySubscription(entry: api_cs.Centries__collection, key: string, destructor: DestructorTree) {
	const collection = entry.parent;
	registerSubscription(getCollectionKeyRegistration(collection, key), destructor);
	register(entry, destructor);
}

export function register(obj: CountableObject, destructor: DestructorTree): void {
	getReferenceCounter(obj).add_registration(destructor);
}

const initializedObjects = new WeakSet<SubscribableObject>();
function initializeSubscribableObject(
	object: SubscribableObject,
	node: api_cs.Cnode,
	destructor: DestructorTree
): void {
	register(object, destructor);
	if (initializedObjects.has(object)) return;
	initializedObjects.add(object);
	isSubscribable(node).subscribe({
		update: (subscribable) => {
			switch (subscribable) {
				case false:
					removeSubscriptionCounter(object);
					break;
				case true:
					getSubscriptionCounter(object);
					break;
			}
		},
	});
}

export function registerText(
	definition: api_app.Ctext__type__property,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Ctext__type__property {
	const text = addTextAttribute(definition, node);
	initializeSubscribableObject(text, node, destructor);
	return text;
}

export function registerNumber(
	definition: api_app.Cnumber__type__property,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Cnumber__type__property {
	const number = addNumberAttribute(definition, node);
	initializeSubscribableObject(number, node, destructor);
	return number;
}

export function registerFile(
	definition: api_app.Cfile,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Cfile__type__property {
	const file = addFileAttribute(definition, node);
	initializeSubscribableObject(file, node, destructor);
	return file;
}

export function registerGroup(
	definition: api_app.Cgroup__type__property,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Cgroup__type__property {
	const group = addGroupAttribute(definition, node);
	initializeSubscribableObject(group, node, destructor);
	return group;
}

export function registerStategroup(
	definition: api_app.Cstate_group,
	node: api_cs.Cnode,
	destructor: DestructorTree,
	remote_state?: api_app.Cstates__state_group
): api_cs.Cstate_group__type__property {
	const stategroup = addStategroupAttribute(definition, node, remote_state);
	initializeSubscribableObject(stategroup, node, destructor);
	return stategroup;
}

export function registerCollection(
	definition: api_app.Ccollection__type__property,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Ccollection {
	const collection = addCollectionAttribute(definition, node);
	registerCollectionUpdates(collection, destructor);
	return collection;
}

export function registerCollectionEntries(collection: api_cs.Ccollection, destructor: DestructorTree): void {
	const registration = getCollectionEntriesRegistration(collection);
	initializeSubscribableObject(registration, collection.parent.parent.parent, destructor);
}

export function registerCollectionUpdates(collection: api_cs.Ccollection, destructor: DestructorTree): void {
	const registration = getCollectionUpdatesRegistration(collection);
	initializeSubscribableObject(registration, collection.parent.parent.parent, destructor);
}

const joinedCollections = new WeakMap<api_cs.Ccollection, DestructorSet<DestructorTree>>();
export function registerJoin(
	collection: api_cs.Ccollection,
	registering_destructor: DestructorTree
): api_cs.Cyes__joined {
	let destructor_set = joinedCollections.get(collection);
	destructor_set?.add(registering_destructor);

	let joined: api_cs.Cyes__joined;
	switch (collection.properties.joined.state.name) {
		case "no": {
			destructor_set?.destroy();
			destructor_set = new DestructorSet(false);
			joinedCollections.set(collection, destructor_set);
			destructor_set.add(registering_destructor);
			const destructor = new DestructorTree();
			addChild(destructor_set, destructor);

			collection.properties.joined.set("yes", {
				"amount of entries": 0,
				"is empty": "yes",
				"joined collection": "unresolved",
				entries: {},
			});
			joined = collection.properties.joined.cast("yes");
			const application_collection = collection.inferences.collection_type();
			const has_reference = application_collection.properties.key_property.ref.properties.has_reference.cast("yes");
			const referenced_collection = has_reference.properties.expression.output.collection();
			const target_collection = Collection.resolveMatrixSourceCollection(
				collection,
				has_reference.properties.expression
			).pipe(
				step.sync((context, source_destructor) => {
					registerCollectionEntries(context, source_destructor);
					joined.properties.joined_collection.set("resolved", {
						"referenced node": createClientIdPathFromNode(context.component_root),
						collection: referenced_collection.parent.parent.key_value,
					});
					return context;
				})
			)();
			onScalarValue(
				target_collection,
				(context) => {
					if (!isResolved(context)) {
						switch (joined.properties.joined_collection.state.name) {
							case "resolved": {
								for (const [key, entry] of joined.properties.entries) {
									switch (entry.properties.referenced_entry.state.name) {
										case "entry": {
											switch (entry.properties.collection_entry.state.name) {
												case "entry":
													entry.properties.referenced_entry.set("none", {});
													break;
												case "none":
													joined.properties.entries.delete(key);
													break;
											}
											break;
										}
										case "none":
											break;
									}
								}
								joined.properties.joined_collection.set("unresolved", {});
								break;
							}
							case "unresolved":
								break;
						}
					}
				},
				(registration, subscription) => destructor.onDestroy(() => registration.unsubscribe(subscription))
			);
			applyCollectionJoin(joined, destructor);
			break;
		}
		case "yes":
			joined = collection.properties.joined.state.node;
			break;
	}
	getReferenceCounter(joined).add_registration(registering_destructor);
	return joined;
}

export function registerCommand(
	definition: api_app.Cglobal,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Coperation__type__attributes {
	const command = addCommandAttribute(definition, node);
	initializeSubscribableObject(command.properties.type.cast("command"), node, destructor);
	return command;
}

export function registerAction(
	definition: api_app.Caction,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Coperation__type__attributes {
	const action = addActionAttribute(definition, node);
	getReferenceCounter(action.properties.type.cast("action")).add_registration(destructor);
	return action;
}

export function registerEntry(entry: api_cs.Centries__collection, destructor: DestructorTree): void {
	register(entry, destructor);
	iEntry(entry).register(destructor);
}

function walkIdPath(id_path: api_app_id.Cid_path, node: api_cs.Cnode, destructor: DestructorTree): api_cs.Cnode {
	switch (id_path.properties.has_steps.state.name) {
		case "no":
			return node;
		case "yes": {
			const has_steps = id_path.properties.has_steps.state.node;
			const node_type = node.input.node_type();
			let tail_node: api_cs.Cnode;
			switch (has_steps.properties.type.state.name) {
				case "collection entry": {
					const type = has_steps.properties.type.state.node;
					const definition = node_type.properties.attributes
						.get(type.properties.collection)!
						.properties.type.cast("property")
						.properties.type.cast("collection");
					const collection = registerCollection(definition, node, destructor);
					const entry = addRemoteEntry(collection, type.properties.id);
					registerEntry(entry, destructor);
					tail_node = entry.properties.node;
					break;
				}
				case "group": {
					const type = has_steps.properties.type.state.node;
					const definition = node_type.properties.attributes
						.get(type.properties.group)!
						.properties.type.cast("property")
						.properties.type.cast("group");
					const group_property = registerGroup(definition, node, destructor);
					tail_node = group_property.properties.node;
					break;
				}
				case "state": {
					const type = has_steps.properties.type.state.node;
					const state_name = type.properties.state;
					const definition = node_type.properties.attributes
						.get(type.properties.state_group)!
						.properties.type.cast("property")
						.properties.type.cast("state group");
					const remote_state = definition.properties.states.get(state_name)!;
					const stategroup_property = registerStategroup(definition, node, destructor, remote_state);
					tail_node = stategroup_property.properties.states.get(state_name)!.properties.node;
					break;
				}
				default:
					unexpectedState(has_steps.properties.type.state);
			}
			return walkIdPath(has_steps.properties.tail, tail_node, destructor);
		}
		default:
			unexpectedState(id_path.properties.has_steps.state);
	}
}

export function registerNewNode(node: api_cs.Cnode, destructor: DestructorTree): void {
	const node_type = node.input.node_type();
	for (const gui_attribute of node_type.properties.attributes.values()) {
		switch (gui_attribute.properties.type.state.name) {
			case "reference set":
				break;
			case "action":
				break;
			case "command":
				break;
			case "property": {
				const property_type = gui_attribute.properties.type.state.node;
				const kind = property_type.properties.type.state.node.output.value_source();
				switch (kind.variant.name) {
					case "derived":
						break;
					case "base":
						switch (property_type.properties.type.state.name) {
							case "text": {
								const text_def = property_type.properties.type.state.node;
								registerText(text_def, node, destructor);
								break;
							}
							case "number": {
								const number_def = property_type.properties.type.state.node;
								registerNumber(number_def, node, destructor);
								break;
							}
							case "file": {
								const file_def = property_type.properties.type.state.node;
								registerFile(file_def, node, destructor);
								break;
							}
							case "group": {
								const group_def = property_type.properties.type.state.node;
								const group_property = registerGroup(group_def, node, destructor);
								registerNewNode(group_property.properties.node, destructor);
								break;
							}
							case "state group": {
								const stategroup_def = property_type.properties.type.state.node;
								const stategroup_property = registerStategroup(stategroup_def, node, destructor);
								for (const state of stategroup_property.properties.states.values()) {
									registerNewNode(state.properties.node, destructor);
								}
								break;
							}
							case "collection": {
								const collection_def = property_type.properties.type.state.node;
								addCollectionAttribute(collection_def, node);
								break;
							}
							default:
								unexpectedState(property_type.properties.type.state);
						}
						break;
					default:
						unexpectedState(kind.variant);
				}
				break;
			}
			default:
				unexpectedState(gui_attribute.properties.type.state);
		}
	}
}

export function registerIdPath(id_path_builder: IdPathBuilder, destructor: DestructorTree): api_cs.Cnode {
	const decorated_id_path = api_app_id.Capplication_identifier.create({
		path: id_path_builder.build(),
	}).properties.path;
	return walkIdPath(decorated_id_path, client_state.properties.root, destructor);
}

function backtrackCommonAncestorInIdPathAndNode(
	node: api_cs.Cnode,
	id_path_builder: IdPathBuilder
): [api_cs.Cnode, Raw_Id_Path_Head] {
	let id_path = id_path_builder.build();
	let result_id_path: Raw_Id_Path_Head = id_path;
	let node_id_path = createIdPathFromNode(node);

	// eslint-disable-next-line no-constant-condition
	while (true) {
		switch (node_id_path["has steps"][0]) {
			case "no":
				return [node, result_id_path];
			case "yes": {
				const node_step = node_id_path["has steps"][1];
				switch (id_path["has steps"][0]) {
					case "no":
						node = node.input.position().input.parent().cast("node");
						node_id_path = node_step.tail;
						break;
					case "yes": {
						const id_step = id_path["has steps"][1];
						if (stepsAreEqual(id_step, node_step)) {
							node_id_path = node_step.tail;
							id_path = id_step.tail;
							result_id_path = id_path;
						} else {
							result_id_path = id_path;
							id_path = { "has steps": ["no", {}] };
						}
						break;
					}
				}
				break;
			}
		}
	}
}

export function registerRelativeIdPath(
	id_path_builder: IdPathBuilder,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Cnode {
	const [ancestor_node, id_path_from_ancestor] = backtrackCommonAncestorInIdPathAndNode(node, id_path_builder);
	return walkIdPath(
		api_app_id.Capplication_identifier.create({ path: id_path_from_ancestor }).properties.path,
		ancestor_node,
		destructor
	);
}

export function registerNodeContext(node: api_cs.Cnode, destructor: DestructorTree): void {
	const position = node.input.position();
	switch (position.variant.name) {
		case "root":
			break;
		case "group": {
			const group = position.variant.definition;
			const definition = group.inferences.group_type();
			const node = group.component_root;
			registerNodeContext(node, destructor);
			registerGroup(definition, node, destructor);
			break;
		}
		case "state": {
			const state = position.variant.definition;
			const definition = state.parent.inferences.state_group_type();
			const node = state.component_root;
			registerNodeContext(node, destructor);
			registerStategroup(definition, node, destructor);
			break;
		}
		case "entity": {
			const entity = position.variant.definition;
			switch (entity.variant.name) {
				case "root":
					break;
				case "entry": {
					const entry = entity.variant.definition;
					const node = entry.component_root;
					registerNodeContext(node, destructor);
					registerEntry(entry, destructor);
					break;
				}
				default:
					unexpectedState(entity.variant);
			}
			break;
		}
		default:
			unexpectedState(position.variant);
	}
}

//TODO use expressions.ts function instead of this
export function registerObjectPathTail(
	path: api_app.Cobject_path_tail,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Cnode {
	let stategroup: api_cs.Cstate_group__type__property | undefined;
	while (path.properties.has_steps.state.name === "yes") {
		const has_steps = path.properties.has_steps.state.node;
		switch (has_steps.properties.type.state.name) {
			case "property value": {
				const $ = has_steps.properties.type.state.node;
				switch ($.properties.property.properties.property.ref.properties.type.state.name) {
					case "group": {
						const group_type = $.properties.property.properties.property.ref.properties.type.state.node;
						const group = registerGroup(group_type, node, destructor);
						node = group.properties.node;
						break;
					}
					case "state group": {
						const stategroup_type = $.properties.property.properties.property.ref.properties.type.state.node;
						stategroup = registerStategroup(stategroup_type, node, destructor);
						break;
					}
					case "text":
						throw new Error("unexpected step in object path tail");
					case "number":
						throw new Error("unexpected step in object path tail");
					case "file":
						throw new Error("unexpected step in object path tail");
					case "collection":
						throw new Error("unexpected step in object path tail");
					default:
						unexpectedState($.properties.property.properties.property.ref.properties.type.state);
				}
				break;
			}
			case "state":
				if (stategroup !== undefined) {
					const state_name = has_steps.properties.type.state.node.properties.state.entry;
					node = stategroup.properties.states.get(state_name)!.properties.node;
					stategroup = undefined;
				} else {
					throw new Error("unexpected step in object path tail");
				}
				break;
			case "parent":
				throw new Error("unexpected step in object path tail");
			case "dependency":
				throw new Error("unexpected step in object path tail");
			case "reference set":
				throw new Error("unexpected step in object path tail");
			default:
				unexpectedState(has_steps.properties.type.state);
		}
		path = has_steps.properties.tail;
	}
	return node;
}

export function registerModelBindingPropertyPath(
	path: api_views.Cmodel_binding_property_path,
	node: api_cs.Cnode,
	destructor: DestructorTree
): api_cs.Cnode {
	let has_steps = path.properties.has_steps.state;
	while (has_steps.name === "yes") {
		const type = has_steps.node.properties.type.state;
		switch (type.name) {
			case "group":
				node = registerGroup(type.node.properties.group.ref.inferences.group(), node, destructor).properties.node;
				break;
			case "parent":
				node = node.input.position().output.parent().cast("node");
				break;
			case "state group":
				node = registerStategroup(
					type.node.properties.state_group.ref.inferences.state_group(),
					node,
					destructor
				).properties.states.get(type.node.properties.state.entry)!.properties.node;
		}
		has_steps = has_steps.node.properties.tail.properties.has_steps.state;
	}
	return node;
}

export function registerQueryEntry(
	query_entry_status: api_cs.Cquery_entry_status,
	destructor: DestructorTree
): api_cs.Cnode {
	let node: api_cs.Cnode;
	const query = query_entry_status.input.query();
	const configuration = (query as any).__configuration as api_views.Cgui_widget_configuration_node;
	switch (query_entry_status.properties.initialized.state.name) {
		case "unknown":
			throw new Error("Can not register query entry with unknown location");
		case "no": {
			const $ = query_entry_status.properties.initialized.state.node;
			const id_path_builder = createIdPathBuilderFromClientIdPath($.properties.referenced_node);
			const entry_destructor = new DestructorTree();
			node = registerIdPath(id_path_builder, entry_destructor);
			registerGuiWidgetConfigurationNode(configuration, node, entry_destructor);
			query_entry_status.properties.initialized.set("yes", {
				"referenced node": createClientIdPathFromNode(node),
			});
			Query.storeEntryId(query_entry_status, id_path_builder);
			const reference_counter = getReferenceCounter(query_entry_status.properties.initialized.cast("yes"));
			reference_counter.add_registration(destructor);
			reference_counter.onEmpty(() => {
				entry_destructor.destroy();
				if (!query_entry_status.destroyed) {
					query_entry_status.properties.initialized.set("no", {
						"referenced node": Query.getEntryId(query_entry_status).build(),
					});
				}
			});
			node.onDestroy(() => {
				entry_destructor.destroy();
				if (!query_entry_status.destroyed) {
					query_entry_status.properties.initialized.set("no", {
						"referenced node": Query.getEntryId(query_entry_status).build(),
					});
				}
			});
			break;
		}
		case "yes": {
			const $ = query_entry_status.properties.initialized.state.node;
			const reference_counter = getReferenceCounter($);
			node = $.properties.referenced_node.output.result_node();
			if (reference_counter.value <= 0) {
				const entry_destructor = new DestructorTree();
				registerNodeContext(node, entry_destructor);
				reference_counter.onEmpty(() => entry_destructor.destroy(), /*persist=*/ false, /*skip_first=*/ true);
			}
			reference_counter.add_registration(destructor);
			break;
		}
		default:
			unexpectedState(query_entry_status.properties.initialized.state);
	}
	return node;
}

type StategroupRegistrant = ["stategroup", { def: api_app.Cstate_group; states: Map<string, Registrant> }];
type AttributeRegistrant =
	| ["command", { def: api_app.Cglobal }]
	| ["action", { def: api_app.Caction }]
	| [
			"text",
			{
				def: api_app.Ctext__type__property;
				reference?: Set<api_views.Cgui_widget_configuration_node>;
			}
	  ]
	| ["number", { def: api_app.Cnumber__type__property }]
	| ["file", { def: api_app.Cfile }]
	| ["group", { def: api_app.Cgroup__type__property; node: Registrant }]
	| StategroupRegistrant
	| [
			"collection",
			{
				def: api_app.Ccollection__type__property;
				entries?: Set<api_views.Cgui_widget_configuration_node>;
				join: boolean;
			}
	  ];

class Registrant {
	attributes = new Map<string, AttributeRegistrant>();
	parent: Registrant | undefined;
	entity: boolean;
}

const gui_widget_configuration_node_registrants = new Map<api_views.Cgui_widget_configuration_node, Registrant>();

function applyRegistrant(registrant: Registrant, node: api_cs.Cnode, destructor: DestructorTree): void {
	for (const att of registrant.attributes.values()) {
		switch (att[0]) {
			case "command": {
				const def = att[1].def;
				registerCommand(def, node, destructor);
				break;
			}
			case "action": {
				const def = att[1].def;
				registerAction(def, node, destructor);
				break;
			}
			case "text": {
				const $ = att[1];
				const text = registerText($.def, node, destructor);
				if ($.reference) {
					switch (text.properties.navigable.state.name) {
						case "no":
							break;
						case "yes": {
							const configurations = $.reference;
							const reference = text.properties.navigable.state.node;
							let referenced_node_destructor: DestructorTree | undefined;
							const referenced_node = iReference(reference).referencedNode;
							const subscription = referenced_node.subscribe({
								update: (referenced_node) => {
									if (referenced_node instanceof api_cs.Cnode) {
										referenced_node_destructor?.destroy();
										referenced_node_destructor = new DestructorTree();
										const entity = referenced_node.input.entity().output.node();
										for (const config of configurations) {
											registerGuiWidgetConfigurationNode(config, entity, referenced_node_destructor);
										}
									}
								},
							});
							destructor.onDestroy(() => referenced_node.unsubscribe(subscription));
							break;
						}
					}
				}
				break;
			}
			case "number": {
				const def = att[1].def;
				registerNumber(def, node, destructor);
				break;
			}
			case "file": {
				const def = att[1].def;
				registerFile(def, node, destructor);
				break;
			}
			case "group": {
				const $ = att[1];
				const group = registerGroup($.def, node, destructor);
				applyRegistrant($.node, group.properties.node, destructor);
				break;
			}
			case "stategroup": {
				const $ = att[1];
				const stategroup = registerStategroup($.def, node, destructor);
				for (const [state_name, state_tree] of $.states) {
					applyRegistrant(state_tree, stategroup.properties.states.get(state_name)!.properties.node, destructor);
				}
				break;
			}
			case "collection": {
				const $ = att[1];
				const collection = registerCollection($.def, node, destructor);
				if ($.entries) {
					const configurations = $.entries;
					const entries_registration = getCollectionEntriesRegistration(collection);
					for (const config of configurations) {
						applyEntriesRegistrant(
							getGuiWidgetConfigurationNodeRegistrant(config),
							entries_registration.node,
							collection,
							destructor
						);
					}
					destructor.subscribeToCollection(
						collection.properties.entries,
						{
							on_add: (entry, destructor) => {
								for (const config of configurations) {
									registerGuiWidgetConfigurationNode(config, entry.properties.node, destructor);
								}
							},
						},
						/*init=*/ true
					);
					if ($.join) {
						registerCollectionEntries(collection, destructor);
						registerJoin(collection, destructor);
					}
				}
				break;
			}
		}
	}
	if (registrant.parent) {
		applyRegistrant(registrant.parent, node.input.position().output.parent().cast("node"), destructor);
	}
	if (registrant.entity) {
		const entity = node.input.entity();
		switch (entity.variant.name) {
			case "entry": {
				const entry = entity.variant.definition;
				registerEntry(entry, destructor);
				break;
			}
			case "root":
				break;
		}
	}
}

function applyEntriesRegistrant(
	registrant: Registrant,
	entries_registration_node: EntriesRegistrationNode,
	collection: api_cs.Ccollection,
	destructor: DestructorTree
): void {
	for (const [name, att] of registrant.attributes) {
		switch (att[0]) {
			case "command": {
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["command", new EntriesRegistrationCommand(collection)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				const entries_registration_command = entries_registration_attribute[1] as EntriesRegistrationGroup;
				initializeSubscribableObject(entries_registration_command, collection.parent.parent.parent, destructor);
				break;
			}
			case "action":
				break;
			case "text": {
				const $ = att[1];
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["text", new EntriesRegistrationText(collection)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				const entries_registration_text = entries_registration_attribute[1] as EntriesRegistrationText;
				initializeSubscribableObject(entries_registration_text, collection.parent.parent.parent, destructor);
				if (!entries_registration_text.reference && $.reference !== undefined) {
					entries_registration_text.reference = true;
				}
				break;
			}
			case "number": {
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["number", new EntriesRegistrationNumber(collection)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				const entries_registration_number = entries_registration_attribute[1] as EntriesRegistrationGroup;
				initializeSubscribableObject(entries_registration_number, collection.parent.parent.parent, destructor);
				break;
			}
			case "file": {
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["file", new EntriesRegistrationFile(collection)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				const entries_registration_file = entries_registration_attribute[1] as EntriesRegistrationGroup;
				initializeSubscribableObject(entries_registration_file, collection.parent.parent.parent, destructor);
				break;
			}
			case "group": {
				const $ = att[1];
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["group", new EntriesRegistrationGroup(collection)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				const entries_registration_group = entries_registration_attribute[1] as EntriesRegistrationGroup;
				initializeSubscribableObject(entries_registration_group, collection.parent.parent.parent, destructor);
				applyEntriesRegistrant($.node, entries_registration_group.node, collection, destructor);
				break;
			}
			case "stategroup": {
				const $ = att[1];
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["stategroup", new EntriesRegistrationStategroup(collection, $.def)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				const entries_registration_stategroup = entries_registration_attribute[1] as EntriesRegistrationStategroup;
				initializeSubscribableObject(entries_registration_stategroup, collection.parent.parent.parent, destructor);
				for (const [state_name, state_registrant] of $.states) {
					let entries_registration_state = entries_registration_stategroup.states.get(state_name);
					if (!entries_registration_state) {
						entries_registration_state = new Map<string, EntriesRegistrationAttribute>();
						entries_registration_stategroup.states.set(state_name, entries_registration_state);
					}
					applyEntriesRegistrant(state_registrant, entries_registration_state, collection, destructor);
				}
				break;
			}
			case "collection": {
				const $ = att[1];
				let entries_registration_attribute = entries_registration_node.get(name);
				if (!entries_registration_attribute) {
					entries_registration_attribute = ["collection", new EntriesRegistrationCollection(collection)];
					entries_registration_node.set(name, entries_registration_attribute);
				}
				getReferenceCounter(entries_registration_attribute[1]).add_registration(destructor);
				const entries_registration_collection = entries_registration_attribute[1] as EntriesRegistrationCollection;
				if ($.entries) {
					initializeSubscribableObject(
						entries_registration_collection.entries,
						collection.parent.parent.parent,
						destructor
					);
					for (const config of $.entries) {
						applyEntriesRegistrant(
							getGuiWidgetConfigurationNodeRegistrant(config),
							entries_registration_collection.entries.node,
							collection,
							destructor
						);
					}
				} else {
					initializeSubscribableObject(
						entries_registration_collection.updates,
						collection.parent.parent.parent,
						destructor
					);
				}
				break;
			}
			default:
				unexpectedState(att[0]);
		}
	}
}

export function registerGuiWidgetConfigurationNode(
	configuration: api_views.Cgui_widget_configuration_node,
	node: api_cs.Cnode,
	destructor: DestructorTree
): void {
	const registrant = getGuiWidgetConfigurationNodeRegistrant(configuration);
	applyRegistrant(registrant, node, destructor);
}

function getGuiWidgetConfigurationNodeRegistrant(configuration: api_views.Cgui_widget_configuration_node): Registrant {
	let registrant = gui_widget_configuration_node_registrants.get(configuration);
	if (!registrant) {
		registrant = createGuiWidgetConfigurationNodeRegistrant(configuration, new Registrant());
		gui_widget_configuration_node_registrants.set(configuration, registrant);
	}
	return registrant;
}

function createModelBindingPropertyPathRegistrant(
	path: api_views.Cmodel_binding_property_path,
	registrant: Registrant
): Registrant {
	let has_steps = path.properties.has_steps.state;
	while (has_steps.name === "yes") {
		const type = has_steps.node.properties.type.state;
		switch (type.name) {
			case "group":
				registrant = createGroupRegistrant(type.node.properties.group.ref.inferences.group(), registrant);
				break;
			case "parent":
				registrant = createParentRegistrant(registrant);
				break;
			case "state group":
				registrant = createStateRegistrant(
					type.node.properties.state_group.ref.inferences.state_group(),
					type.node.properties.state.entry,
					registrant
				);
		}
		has_steps = has_steps.node.properties.tail.properties.has_steps.state;
	}
	return registrant;
}

function createQueryPathStepRegistrant(path: api_views.Cquery_path_step, registrant: Registrant): Registrant {
	while (path.properties.has_steps.state.name === "yes") {
		const yes = path.properties.has_steps.state.node;
		switch (yes.properties.type.state.name) {
			case "group": {
				const $ = yes.properties.type.state.node;
				registrant = createGroupRegistrant($.properties.group.ref.inferences.group(), registrant);
				break;
			}
			case "state": {
				const $ = yes.properties.type.state.node;
				registrant = createStateRegistrant(
					$.properties.state_group.ref.inferences.state_group(),
					$.properties.state.entry,
					registrant
				);
				break;
			}
			default:
				return unexpectedState(yes.properties.type.state);
		}
		path = yes.properties.tail;
	}
	return registrant;
}

function createObjectPathTailRegistrant(path: api_app.Cobject_path_tail, registrant: Registrant): Registrant {
	let stategroup: api_app.Cstate_group | undefined;
	while (path.properties.has_steps.state.name === "yes") {
		const has_steps = path.properties.has_steps.state.node;
		switch (has_steps.properties.type.state.name) {
			case "property value": {
				const $ = has_steps.properties.type.state.node;
				switch ($.properties.property.properties.property.ref.properties.type.state.name) {
					case "group": {
						const group = $.properties.property.properties.property.ref.properties.type.state.node;
						registrant = createGroupRegistrant(group, registrant);
						break;
					}
					case "state group":
						stategroup = $.properties.property.properties.property.ref.properties.type.state.node;
						break;
					case "text":
						throw new Error("unexpected step for conditional path");
					case "number":
						throw new Error("unexpected step for conditional path");
					case "file":
						throw new Error("unexpected step for conditional path");
					case "collection":
						throw new Error("unexpected step for conditional path");
					default:
						unexpectedState($.properties.property.properties.property.ref.properties.type.state);
				}
				break;
			}
			case "state":
				if (stategroup !== undefined) {
					const state_name = has_steps.properties.type.state.node.properties.state.entry;
					registrant = createStateRegistrant(stategroup, state_name, registrant);
					stategroup = undefined;
				} else {
					throw new Error("unexpected step for conditional path");
				}
				break;
			case "parent":
				throw new Error("unexpected step for conditional path");
			case "dependency":
				throw new Error("unexpected step for conditional path");
			case "reference set":
				throw new Error("unexpected step for conditional path");
			default:
				unexpectedState(has_steps.properties.type.state);
		}
		path = has_steps.properties.tail;
	}
	return registrant;
}

function createTextRegistrant(def: api_app.Ctext__type__property, registrant: Registrant, reference: false): void;
function createTextRegistrant(
	def: api_app.Ctext__type__property,
	registrant: Registrant,
	reference: true,
	reference_config?: api_views.Cgui_widget_configuration_node
): void;
function createTextRegistrant(
	def: api_app.Ctext__type__property,
	registrant: Registrant,
	reference: boolean,
	reference_config?: api_views.Cgui_widget_configuration_node
): void {
	const text_registrant = registrant.attributes.get(def.parent.parent.key);
	if (text_registrant && text_registrant[0] === "text") {
		if (reference) {
			if (!text_registrant[1].reference) {
				text_registrant[1].reference = new Set();
			}
			if (reference_config) {
				text_registrant[1].reference.add(reference_config);
			}
		}
	} else {
		if (reference) {
			registrant.attributes.set(def.parent.parent.key, [
				"text",
				{ def: def, reference: new Set(reference_config ? [reference_config] : []) },
			]);
		} else {
			registrant.attributes.set(def.parent.parent.key, ["text", { def: def }]);
		}
	}
}

function createStategroupRegistrant(def: api_app.Cstate_group, registrant: Registrant): StategroupRegistrant {
	let stategroup_registrant = registrant.attributes.get(def.parent.parent.key);
	if (!stategroup_registrant || stategroup_registrant[0] !== "stategroup") {
		stategroup_registrant = ["stategroup", { def: def, states: new Map<string, Registrant>() }];
		registrant.attributes.set(def.parent.parent.key, stategroup_registrant);
	}
	return stategroup_registrant;
}

function createStateRegistrant(def: api_app.Cstate_group, state_name: string, registrant: Registrant): Registrant {
	const stategroup_registrant = createStategroupRegistrant(def, registrant);
	let state_registrant = stategroup_registrant[1].states.get(state_name);
	if (!state_registrant) {
		state_registrant = new Registrant();
		stategroup_registrant[1].states.set(state_name, state_registrant);
	}
	return state_registrant;
}

function createGroupRegistrant(def: api_app.Cgroup__type__property, registrant: Registrant): Registrant {
	let group_registrant = registrant.attributes.get(def.parent.parent.key);
	if (!group_registrant || group_registrant[0] !== "group") {
		group_registrant = ["group", { def: def, node: new Registrant() }];
		registrant.attributes.set(def.parent.parent.key, group_registrant);
	}
	return group_registrant[1].node;
}

function createCollectionRegistrant(
	def: api_app.Ccollection__type__property,
	registrant: Registrant,
	join: boolean,
	entries_config?: api_views.Cgui_widget_configuration_node
) {
	const collection_name = def.parent.parent.key;
	const collection_registrant = registrant.attributes.get(collection_name);
	if (collection_registrant && collection_registrant[0] === "collection") {
		if (entries_config) {
			if (!collection_registrant[1].entries) {
				collection_registrant[1].entries = new Set();
			}
			collection_registrant[1].entries.add(entries_config);
		}
		if (join === true) {
			collection_registrant[1].join = true;
		}
	} else {
		if (entries_config) {
			registrant.attributes.set(collection_name, [
				"collection",
				{ def: def, entries: new Set([entries_config]), join: join },
			]);
		} else {
			registrant.attributes.set(collection_name, ["collection", { def: def, join: join }]);
		}
	}
}

function createParentRegistrant(registrant: Registrant): Registrant {
	let parent = registrant.parent;
	if (parent === undefined) {
		parent = new Registrant();
		registrant.parent = parent;
	}
	return parent;
}

function createGuiWidgetConfigurationNodeRegistrant(
	configuration: api_views.Cgui_widget_configuration_node,
	registrant: Registrant
): Registrant {
	for (const $ of configuration.properties.configuration.values()) {
		const attribute = $.properties.attribute;
		switch (attribute.properties.type.state.name) {
			case "configuration": {
				const configuration = attribute.properties.type.state.node;
				switch (configuration.properties.type.state.name) {
					case "list": {
						const $ = configuration.properties.type.state.node;
						let widget_list = $.properties.list;
						while (widget_list.properties.has_steps.state.name === "yes") {
							const $ = widget_list.properties.has_steps.state.node;
							createGuiWidgetConfigurationNodeRegistrant($.properties.configuration, registrant);
							widget_list = $.properties.tail;
						}
						break;
					}
					case "number":
						break;
					case "state group": {
						const $ = configuration.properties.type.state.node;
						createGuiWidgetConfigurationNodeRegistrant($.properties.configuration, registrant);
						break;
					}
					case "states list": {
						const $ = configuration.properties.type.state.node;
						for (const state of $.properties.states) {
							createGuiWidgetConfigurationNodeRegistrant(state[1].properties.configuration, registrant);
						}
						break;
					}
					case "text":
						break;
					default:
						unexpectedState(configuration.properties.type.state);
				}
				break;
			}
			case "model binding": {
				const model_binding = attribute.properties.type.state.node;
				switch (model_binding.properties.type.state.name) {
					case "action": {
						const $ = model_binding.properties.type.state.node;
						const action_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						action_context_registrant.attributes.set($.properties.action.entry, [
							"action",
							{ def: $.properties.action.ref.inferences.action_type() },
						]);
						break;
					}
					case "collection": {
						const $ = model_binding.properties.type.state.node;
						let join: boolean;
						switch ($.properties.join.state.name) {
							case "no":
								join = false;
								break;
							case "yes": {
								join = true;
								break;
							}
							default:
								unexpectedState($.properties.join.state);
						}
						const definition = $.properties.collection.ref.inferences.collection();
						const collection_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						createCollectionRegistrant(definition, collection_context_registrant, join, $.properties.configuration);
						break;
					}
					case "command": {
						const $ = model_binding.properties.type.state.node;
						const command_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						command_context_registrant.attributes.set($.properties.command.entry, [
							"command",
							{ def: $.properties.command.ref.inferences.global_type() },
						]);
						break;
					}
					case "current node": {
						const $ = model_binding.properties.type.state.node;
						const current_node_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						createGuiWidgetConfigurationNodeRegistrant($.properties.configuration, current_node_context_registrant);
						break;
					}
					case "entity": {
						const $ = model_binding.properties.type.state.node;
						registrant.entity = true;
						createGuiWidgetConfigurationNodeRegistrant($.properties.configuration, registrant);
						break;
					}
					case "file": {
						const $ = model_binding.properties.type.state.node;
						const definition = $.properties.property.ref.inferences.file();
						const file_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						file_context_registrant.attributes.set($.properties.property.entry, ["file", { def: definition }]);
						break;
					}
					case "group": {
						const $ = model_binding.properties.type.state.node;
						const definition = $.properties.group.ref.inferences.group();
						createGuiWidgetConfigurationNodeRegistrant(
							$.properties.configuration,
							createGroupRegistrant(definition, registrant)
						);
						break;
					}
					case "number": {
						const $ = model_binding.properties.type.state.node;
						const definition = $.properties.property.ref.inferences.number();
						const number_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						number_context_registrant.attributes.set($.properties.property.entry, ["number", { def: definition }]);
						break;
					}
					case "ordered graph": {
						const $ = model_binding.properties.type.state.node;
						const ordered_graph =
							$.properties.ordered_graph.ref.key.inferences.ordered_graph().properties.ordering_property_path;
						const ordering_state_group_definition =
							ordered_graph.properties.ordering_state_group_path.inferences.state_group();
						const edge_state_name = ordered_graph.properties.ordering_states.properties.edge_state.entry;
						const edge_reference_definition =
							ordered_graph.properties.ordering_states.properties.edge_reference.ref.parent;

						const ordering_context_registrant = createObjectPathTailRegistrant(
							ordered_graph.properties.ordering_state_group_path.properties.head,
							registrant
						);
						const edge_state_registrant = createStateRegistrant(
							ordering_state_group_definition,
							edge_state_name,
							ordering_context_registrant
						);
						createTextRegistrant(edge_reference_definition, edge_state_registrant, true);
						break;
					}
					case "query": {
						const query_binding = model_binding.properties.type.state.node;
						switch (query_binding.properties.query_context.state.name) {
							case "candidates":
								break;
							case "node": {
								const $node = query_binding.properties.query_context.state.node;
								switch ($node.properties.switch_.state.name) {
									case "current node": {
										const $current_node = $node.properties.switch_.state.node;
										const query_path = $current_node.properties.query.inferences.query().parent.properties.path;
										switch (query_path.properties.has_steps.state.name) {
											case "no":
												break;
											case "yes": {
												const $ = query_path.properties.has_steps.state.node;
												// check if there is only one collection step
												switch ($.properties.tail.properties.has_steps.state.name) {
													case "no": {
														const query_context_registrant = createQueryPathStepRegistrant(
															$.properties.head,
															createModelBindingPropertyPathRegistrant(
																$current_node.properties.property_path,
																registrant
															)
														);
														createCollectionRegistrant(
															$.properties.collection.ref.inferences.collection(),
															query_context_registrant,
															false
														);
														break;
													}
													case "yes":
														break;
													default:
														unexpectedState($.properties.tail.properties.has_steps.state);
												}
												break;
											}
											default:
												unexpectedState(query_path.properties.has_steps.state);
										}
										break;
									}
									case "root":
										break;
									default:
										unexpectedState($node.properties.switch_.state);
								}
								break;
							}
							default:
								unexpectedState(query_binding.properties.query_context.state);
						}
						break;
					}
					case "query file":
						break;
					case "query number":
						break;
					case "query stategroup":
						break;
					case "query text":
						break;
					case "report":
						break;
					case "report number parameter":
						break;
					case "report text parameter":
						break;
					case "sort property":
						break;
					case "state": {
						const state_binding = model_binding.properties.type.state.node;
						let state: api_gui.Cstates__state_group__type__property__type__attributes;
						switch (state_binding.properties.state_group_context.state.name) {
							case "state group binding": {
								const $ = state_binding.properties.state_group_context.state.node;
								state = $.properties.state.ref;
								break;
							}
							case "states list": {
								const $ = state_binding.properties.state_group_context.state.node;
								state = $.inferences.state_context().key.ref;
								break;
							}
							default:
								unexpectedState(state_binding.properties.state_group_context.state);
						}
						const state_registrant = createStateRegistrant(state.key.ref.parent, state.key.entry, registrant);
						createGuiWidgetConfigurationNodeRegistrant(state_binding.properties.configuration, state_registrant);
						break;
					}
					case "state group": {
						const $ = model_binding.properties.type.state.node;
						const stategroup_definition = $.properties.state_group.ref.inferences.state_group();
						const property_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						createStategroupRegistrant(stategroup_definition, property_context_registrant);
						createGuiWidgetConfigurationNodeRegistrant($.properties.configuration, property_context_registrant);
						break;
					}
					case "state rule":
						break;
					case "text": {
						const $ = model_binding.properties.type.state.node;
						const property_context_registrant = createModelBindingPropertyPathRegistrant(
							$.properties.property_path,
							registrant
						);
						const text_definition = $.properties.property.ref.inferences.text();
						switch (text_definition.properties.has_reference.state.name) {
							case "no":
								createTextRegistrant(text_definition, property_context_registrant, false);
								break;
							case "yes":
								createTextRegistrant(text_definition, property_context_registrant, true, $.properties.configuration);
								break;
							default:
								unexpectedState(text_definition.properties.has_reference.state);
						}
						break;
					}
					case "window":
						break;
					case "joined entry": {
						//TODO register the widget configuration of the joined entry?
						break;
					}
					case "user":
						break;
					default:
						unexpectedState(model_binding.properties.type.state);
				}
				break;
			}
			case "view":
				break;
			case "widget": {
				const $ = attribute.properties.type.state.node;
				createWidgetExpressionRegistrant($.properties.instance, registrant);
				break;
			}
			case "window": {
				const $ = attribute.properties.type.state.node;
				createGuiWidgetConfigurationNodeRegistrant($.properties.configuration, registrant);
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}
	return registrant;
}

function createWidgetExpressionRegistrant(expression: api_views.Cwidget_expression, registrant: Registrant) {
	switch (expression.properties.type.state.name) {
		case "group": {
			const $ = expression.properties.type.state.node;
			createWidgetExpressionRegistrant(
				$.properties.next,
				createGroupRegistrant($.properties.group.ref.inferences.group(), registrant)
			);
			break;
		}
		case "state": {
			const $ = expression.properties.type.state.node;
			createWidgetExpressionRegistrant(
				$.properties.next,
				createStateRegistrant(
					$.properties.state_group.ref.inferences.state_group(),
					$.properties.state.entry,
					registrant
				)
			);
			break;
		}
		case "switch": {
			const $ = expression.properties.type.state.node;
			for (const state of $.properties.states.values()) {
				createWidgetExpressionRegistrant(
					state.properties.next,
					createStateRegistrant($.properties.state_group.ref.inferences.state_group(), state.key.entry, registrant)
				);
			}
			break;
		}
		case "parent": {
			const $ = expression.properties.type.state.node;
			createWidgetExpressionRegistrant($.properties.next, createParentRegistrant(registrant));
			break;
		}
		case "widget": {
			const $ = expression.properties.type.state.node;
			switch ($.properties.lazy.state.name) {
				case "no":
					createGuiWidgetConfigurationNodeRegistrant($.properties.widget.properties.configuration, registrant);
					break;
				case "yes":
					break;
				default:
					unexpectedState($.properties.lazy.state);
			}
			break;
		}
		case "reference": {
			const $ = expression.properties.type.state.node;
			createTextRegistrant($.properties.text.ref.inferences.text(), registrant, true);
			break;
		}
		case "reference rule": {
			const $ = expression.properties.type.state.node;
			createTextRegistrant($.properties.text.ref.inferences.text(), registrant, true);
			break;
		}
		case "state context rule": {
			break;
		}
		case "view":
			break;
		default:
			unexpectedState(expression.properties.type.state);
	}
}
